import React, { useEffect } from "react";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Cta from "../components/Cta";
import { useNavigate } from "react-router-dom";
import { AppleCardsCarouselDemo } from "../components/Imagegalary";
import { Helmet } from "react-helmet";

const AboutPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const titleVariant = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 1 } },
  };

  const descriptionVariant = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 1, delay: 0.3 } },
  };

  // Animation for the moving leaf element
  const leafVariant = {
    hidden: { opacity: 0, scale: 0.8, rotate: 0 },
    visible: {
      opacity: 1,
      scale: 1.2,
      rotate: [0, 10, -10, 0],
      x: ["-50%", "50%", "-30%", "40%"],
      transition: {
        repeat: Infinity,
        repeatType: "reverse",
        duration: 5,
        ease: "easeInOut",
        delay: 0.5,
      },
    },
  };

  // Animation for floating bubbles or particles
  const particleVariant = {
    hidden: { opacity: 0, scale: 0.5 },
    visible: {
      opacity: 1,
      scale: 1.5,
      x: ["-100px", "100px"], // Horizontal floating effect
      y: ["-100px", "100px"], // Vertical floating effect
      transition: {
        repeat: Infinity,
        repeatType: "loop",
        duration: 10,
        ease: "easeInOut",
      },
    },
  };
  return (
    <>
      <Helmet>
        <title>About Beyond Bloom | Our Story & Mission</title>
        <meta
          name="description"
          content="Learn more about Beyond Bloom – our story, mission, and commitment to providing premium natural products. Discover what makes us unique in the wellness space."
        />
        <meta
          name="keywords"
          content="Beyond Bloom, About Us, Our Mission, Wellness Commitment, Premium Natural Products, Sustainability, High-Quality Strains"
        />
      </Helmet>

      <Navbar />
      <div className="min-h-screen bg-black text-white">
        {/* Hero Section */}
        <section className="relative flex items-center justify-center h-screen">
          <img
            src="/hero2.jpg"
            alt="Background"
            className="absolute inset-0 w-full h-full object-cover opacity-30 transition-opacity duration-1000"
          />
          <motion.div
            className="z-10 text-center"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.5 }}
          >
            <h1 className="text-6xl lg:text-7xl font-extrabold text-green-400 mb-6 tracking-tight">
              About Beyond Bloom
            </h1>
            <p className="text-lg lg:text-xl text-gray-300 max-w-2xl mx-auto">
              Empowering businesses worldwide with innovative cannabis
              solutions.
            </p>
            <motion.a
              href="#mission"
              className="mt-6 inline-block px-6 py-3 bg-green-400 text-black rounded-lg font-semibold text-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.5 }}
            >
              Learn More
            </motion.a>
          </motion.div>
        </section>

        {/* Mission Section */}
        <section className="py-20 px-6 " >
          <motion.div
            className="container mx-auto text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <h2 className="text-5xl font-semibold text-green-400 mb-6">
              Our Mission
            </h2>
            <p className="text-gray-300 text-xl">
              To revolutionize the global cannabis market with eco-friendly and
              ethically sourced products.
            </p>
          </motion.div>

          {/* Floating Bubbles/Particles */}
          <motion.div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10"
            variants={particleVariant}
            initial="hidden"
            animate="visible"
          >
            <div className="w-12 h-12 bg-blue-200 rounded-full opacity-50"></div>
          </motion.div>

          <motion.div
            className="absolute top-3/4 left-1/4 transform -translate-x-1/2 -translate-y-1/2 z-10"
            variants={particleVariant}
            initial="hidden"
            animate="visible"
          >
            <div className="w-12 h-12 bg-blue-300 rounded-full opacity-50"></div>
          </motion.div>

          {/* Background animation */}
          <motion.div
            className="absolute top-0 left-0 w-full h-full z-0"
            animate={{ backgroundPosition: "200% 0%" }}
            transition={{ repeat: Infinity, duration: 10, ease: "linear" }}
            style={{
              backgroundImage: "url('/path/to/your/background-image.jpg')",
              backgroundSize: "200% 100%",
            }}
          />
        </section>

        {/* Vision Section */}
        <section className="bg-green-900" id="mission">
          <div className="container mx-auto px-6 md:px-12 lg:px-20 py-5">
            <motion.div
              className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              {/* Left Content - Vision Text */}
              <div className="space-y-6 text-left">
                <h2 className="text-4xl md:text-5xl font-extrabold text-white">
                  Our Vision
                </h2>
                <p className="text-gray-300 text-lg font-semibold leading-relaxed">
                  To be a global leader in sustainable cannabis cultivation and
                  distribution, fostering innovation while embracing
                  eco-conscious practices. We aim to cultivate products that
                  promote well-being, support local communities, and pave the
                  way for a greener future.
                </p>
                <p className="text-gray-300 text-lg font-semibold leading-relaxed">
                  Our vision is rooted in respect for the environment and a
                  commitment to providing natural, ethically sourced products
                  that improve lives and empower consumers.
                </p>
              </div>

              {/* Right Content - Vision Image */}
              <motion.div
                className="relative flex justify-center lg:justify-end"
                initial={{ scale: 0.8 }}
                whileHover={{ scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <img
                  src="/home/leaf.png"
                  alt="Vision"
                  className="rounded-lg shadow-lg border border-green-700 w-full max-w-sm md:max-w-md"
                />
              </motion.div>
            </motion.div>
          </div>
        </section>
        <AppleCardsCarouselDemo />

        {/* Why Choose Us Section */}
        <section className="py-16 px-6 bg-gradient-to-r from-green-800 via-green-900 to-black">
          <div className="container mx-auto">
            {/* Section Heading */}
            <motion.h2
              className="text-4xl md:text-5xl font-bold text-white text-center mb-14"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              Why Choose Us
            </motion.h2>

            {/* Feature Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {[...Array(6)].map((_, index) => (
                <motion.div
                  key={index}
                  className="relative bg-white p-8 rounded-xl shadow-xl transform hover:scale-[1.05] transition-all duration-300 hover:shadow-2xl overflow-hidden border border-green-200"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  {/* Background Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-green-500 via-green-600 to-green-700 opacity-10 rounded-xl"></div>

                  {/* Icon Section */}
                  <div className="relative flex justify-center items-center mb-6">
                    <motion.i
                      className="fas fa-leaf text-6xl text-green-600 transition-transform duration-300"
                      initial={{ scale: 0.8 }}
                      whileHover={{ scale: 1 }}
                      transition={{ duration: 0.5 }}
                    ></motion.i>
                  </div>

                  {/* Heading and Description */}
                  <h3 className="relative text-2xl font-semibold text-green-800 text-center mb-4 transition-all duration-300 hover:text-green-600">
                    {
                      [
                        "Innovation at the Core",
                        "Sustainable Practices",
                        "Customer-Centric Solutions",
                        "Timely Delivery",
                        "Industry Expertise",
                        "Cutting-Edge Technology",
                      ][index]
                    }
                  </h3>
                  <p className="relative text-lg text-gray-700 text-center leading-relaxed">
                    {
                      [
                        "We prioritize innovation in everything we do, ensuring our solutions push the boundaries of what's possible.",
                        "Our commitment to sustainability drives us to use eco-friendly materials and practices for a greener future.",
                        "We tailor our solutions to meet the unique needs of our clients, providing personalized, exceptional service.",
                        "We pride ourselves on delivering results on time, ensuring your needs are met when they matter most.",
                        "With years of experience in the industry, we offer insights and expertise that set us apart.",
                        "Leveraging the latest technology, we deliver state-of-the-art solutions that ensure your business stays ahead.",
                      ][index]
                    }
                  </p>

                  {/* Decorative Pattern (Old Design Restored) */}
                  <div className="absolute bottom-0 right-0 w-1/3 h-1/3 bg-green-500 opacity-20 transform rotate-45 rounded-full"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Catalog Download Section */}
        <section className="py-24 bg-black text-white text-center">
          <div className="container mx-auto px-6 md:px-12 lg:px-20">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="text-left">
                <h3 className="text-green-500 text-xl font-bold uppercase mb-2">
                  Our Catalogs
                </h3>
                <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-white mb-4">
                  Our Product <span className="text-green-500">Catalogs</span>{" "}
                  And Details
                </h2>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Our digital catalogs provide an outlook on our products.
                  Beyond Bloom offers a variety of high-quality products and
                  services to enhance modern living spaces. Our commitment to
                  excellence ensures top-tier products curated for a
                  sophisticated lifestyle.
                </p>
                <button
                  onClick={() => navigate("/contact")}
                  className="mt-6 inline-block px-6 py-3 bg-green-400 text-black rounded-lg font-semibold text-lg hover:bg-green-500 transition duration-300"
                >
                  Contact Us
                </button>
              </div>

              {/* Right Content - Image and Download Section */}
              <div className="relative text-center">
                <div className="relative inline-block group">
                  <a href="/BEYONDBLOOMcatalog.pdf" download>
                    <img
                      src="/images/catlog.jpg"
                      alt="Beyond Bloom Catalog"
                      className="w-80 md:w-96 rounded-lg shadow-lg border border-gray-500 transition-transform duration-300 group-hover:scale-105 group-hover:shadow-xl cursor-pointer"
                    />
                  </a>
                  <div className="absolute -right-10 top-1/2 transform -translate-y-1/2 bg-green-500 backdrop-blur-lg border-2 border-green-500 text-black text-lg font-bold px-4 py-2 rotate-90 shadow-md transition-all duration-300 group-hover:opacity-90 group-hover:scale-105">
                    Catalogue
                  </div>
                </div>
                <h4 className="text-lg font-bold text-gray-200 mt-6">
                  Beyond Bloom Collection
                </h4>
                <p className="text-gray-400 text-sm max-w-md mx-auto">
                  To explore our exclusive collection, download the catalog
                  below.
                </p>
                <a href="/BEYONDBLOOMcatalog.pdf" download>
                  <button className="mt-6 inline-block px-6 py-3 bg-green-400 text-black rounded-lg font-semibold text-lg hover:bg-green-500 transition duration-300">
                    Download
                  </button>
                </a>
              </div>
            </div>
          </div>
        </section>

        <Cta />
      </div>

      <Footer />
    </>
  );
};

export default AboutPage;
